import { Label } from "../ui/label";
import { getContrastColor } from "./riskMatrixShared";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../ui/tooltip";
import { useMemo } from "react";

const RiskMatrixPreview = ({ matrix }) => {
  if (!matrix) {
    return null;
  }

  // Helper function to get description or return a default message
  const getDescription = (type, index) => {
    if (!matrix.descriptions) return "No description available";
    
    if (type === "severity" && matrix.descriptions.severities && matrix.descriptions.severities[index]) {
      return matrix.descriptions.severities[index];
    }
    
    if (type === "probability" && matrix.descriptions.probabilities && matrix.descriptions.probabilities[index]) {
      return matrix.descriptions.probabilities[index];
    }
    
    return "No description available";
  };

  // Get combined description for a cell
  const getCombinedDescription = (probIndex, sevIndex) => {
    const probDesc = getDescription("probability", probIndex);
    const sevDesc = getDescription("severity", sevIndex);
    
    const probability = matrix.probabilities[probIndex];
    const severity = matrix.severities[sevIndex];
    const cellValue = matrix.matrix[probIndex][sevIndex];
    
    return (
      <div className="space-y-2">
        <div><strong>Risk Rating:</strong> {cellValue}</div>
        <div><strong>Probability ({probability}):</strong> {probDesc !== "No description available" ? probDesc : "No description"}</div>
        <div><strong>Severity ({severity}):</strong> {sevDesc !== "No description available" ? sevDesc : "No description"}</div>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <Label>{matrix.name}</Label>

      {matrix.isAxesFlipped ? (
        <FlippedMatrixTable matrix={matrix} getDescription={getDescription} getCombinedDescription={getCombinedDescription} />
      ) : (
        <NormalMatrixTable matrix={matrix} getDescription={getDescription} getCombinedDescription={getCombinedDescription} />
      )}
    </TooltipProvider>
  );
};

// Component for normal matrix display (probability as rows, severity as columns)
const NormalMatrixTable = ({ matrix, getDescription, getCombinedDescription }) => {
  return (
    <table className="w-full border-collapse mt-2">
      <thead>
        <tr>
          <th className="border p-2 text-xs text-center align-bottom min-w-[60px] relative" style={{height: 48}}>
            {/* Diagonal divider for axis labels using CSS gradient */}
            <div className="w-full h-full">
              <div className="absolute top-0 left-0 w-full h-full">
                <div className="w-full h-full relative">
                  <div className="absolute top-0 right-0 w-1/2 h-1/2 flex items-end justify-end text-xs p-1">
                    {matrix.diagonalText?.top || 'Severity'}
                  </div>
                  <div className="absolute bottom-0 left-0 w-1/2 h-1/2 flex items-start justify-start text-xs p-1">
                    {matrix.diagonalText?.bottom || 'Probability'}
                  </div>
                  <div
                    className="absolute top-0 left-0 w-full h-full"
                    style={{
                      background:
                        "linear-gradient(to top right, transparent calc(50% - 1px), black, transparent calc(50% + 1px))",
                      pointerEvents: "none",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </th>
          {matrix.severities.map((label, index) => (
            <th key={index} className="border p-2 text-sm">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">{label}</span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>{getDescription("severity", index)}</p>
                </TooltipContent>
              </Tooltip>
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {matrix.matrix.map((row, rowIndex) => (
          <tr key={rowIndex}>
            <td className="border p-2 font-medium text-sm">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">{matrix.probabilities[rowIndex]}</span>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p>{getDescription("probability", rowIndex)}</p>
                </TooltipContent>
              </Tooltip>
            </td>

            {row.map((cell, colIndex) => {
              const backgroundColor = matrix.colors[cell] || "#FFFFFF";
              const textColor = getContrastColor(backgroundColor);

              return (
                <td
                  key={colIndex}
                  className="border p-2 text-center text-sm"
                  style={{
                    backgroundColor,
                    color: textColor,
                  }}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">{cell}</span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {getCombinedDescription(rowIndex, colIndex)}
                    </TooltipContent>
                  </Tooltip>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Component for flipped matrix display (severity as rows, probability as columns)
const FlippedMatrixTable = ({ matrix, getDescription, getCombinedDescription }) => {
  // Create a transposed version of the matrix for display
  const transposedMatrix = useMemo(() => {
    return matrix.matrix[0].map((_, colIndex) => 
      matrix.matrix.map(row => row[colIndex])
    );
  }, [matrix.matrix]);

  return (
    <table className="w-full border-collapse mt-2">
      <thead>
        <tr>
          <th className="border p-2 text-xs text-center align-bottom min-w-[60px] relative" style={{height: 48}}>
            {/* Diagonal divider for axis labels using CSS gradient (flipped) */}
            <div className="w-full h-full">
              <div className="absolute top-0 left-0 w-full h-full">
                <div className="w-full h-full relative">
                  <div className="absolute top-0 right-0 w-1/2 h-1/2 flex items-end justify-end text-xs p-1">
                    {matrix.diagonalText?.top || 'Probability'}
                  </div>
                  <div className="absolute bottom-0 left-0 w-1/2 h-1/2 flex items-start justify-start text-xs p-1">
                    {matrix.diagonalText?.bottom || 'Severity'}
                  </div>
                  <div
                    className="absolute top-0 left-0 w-full h-full"
                    style={{
                      background:
                        "linear-gradient(to top right, transparent calc(50% - 1px), black, transparent calc(50% + 1px))",
                      pointerEvents: "none",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </th>
          {matrix.probabilities.map((label, index) => (
            <th key={index} className="border p-2 text-sm">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">{label}</span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>{getDescription("probability", index)}</p>
                </TooltipContent>
              </Tooltip>
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {transposedMatrix.map((row, rowIndex) => (
          <tr key={rowIndex}>
            <td className="border p-2 font-medium text-sm">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">{matrix.severities[rowIndex]}</span>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p>{getDescription("severity", rowIndex)}</p>
                </TooltipContent>
              </Tooltip>
            </td>

            {row.map((_, colIndex) => {
              // Use the original matrix cell value
              const actualCell = matrix.matrix[colIndex][rowIndex];
              const backgroundColor = matrix.colors[actualCell] || "#FFFFFF";
              const textColor = getContrastColor(backgroundColor);

              return (
                <td
                  key={colIndex}
                  className="border p-2 text-center text-sm"
                  style={{
                    backgroundColor,
                    color: textColor,
                  }}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">{actualCell}</span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {getCombinedDescription(colIndex, rowIndex)}
                    </TooltipContent>
                  </Tooltip>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default RiskMatrixPreview;
